import { type ImageType, type ReviewState, type CatalogIds, type Zhihao, WithoutIdAndReview } from "@briefmarken_katalog/types";
import type { TranslationKey } from "$lib/i18n";
import { StandardEditorModelBase } from "./StandardEditorModelBase.svelte";
import { validateZhihaoField } from "./validateZhihaoField";

interface CollectionItemShape {
  title: string;
  description: string;
  date_of_issue: Date;
  expiry_date?: Date;
  print_run?: number;
  categories?: string[];
  catalog_ids?: CatalogIds;
  zhihao?: Zhihao;
  pictures?: ImageType[];
  stamps: any[];
}

export abstract class CollectionEditorModelBase<TItem extends CollectionItemShape>
  extends StandardEditorModelBase<TItem> {

  title: string = $state("");
  description: string = $state("");
  date_of_issue: Date | null = $state<Date | null>(null);
  expiry_date: Date | null = $state<Date | null>(null);
  print_run: number | null = $state(null);
  categories: string[] = $state<string[]>([]);
  catalog_ids: CatalogIds = $state({});
  zhihao = $state<Zhihao | undefined>(undefined);
  pictures = $state<ImageType[]>([]);
  picturesDirty = $state(false);
  stampIds: string[] = $state<string[]>([]);
  isTitleInvalid = $derived(this.title === "");
  isDateInvalid = $derived(this.date_of_issue === null);
  isStampIdsInvalid = $derived(this.stampIds.length === 0);

  constructor({
    onCreate,
    onReview,
    onDelete = () => {},
    isReadonly = false,
    isReview = false,
    isDelete = false,
    isEdit = false,
    initialValue = null,
    dialogTitle,
    dialogSubtitle,
  }: {
    onCreate: (item: WithoutIdAndReview<TItem>) => void;
    onReview: (item: WithoutIdAndReview<TItem>, state: ReviewState) => void;
    onDelete?: () => void;
    isReadonly?: boolean;
    isReview?: boolean;
    isDelete?: boolean;
    isEdit?: boolean;
    initialValue?: TItem | null;
    dialogTitle: TranslationKey;
    dialogSubtitle: TranslationKey;
  }) {
    super({ onCreate, onReview, onDelete, isReadonly, isReview, isDelete, isEdit,
            hasInitialValue: initialValue !== null, dialogTitle, dialogSubtitle });

    if (initialValue) {
      this.title = initialValue.title;
      this.description = initialValue.description;
      this.date_of_issue = initialValue.date_of_issue;
      this.expiry_date = initialValue.expiry_date ?? null;
      this.print_run = initialValue.print_run ?? null;
      this.categories = initialValue.categories ?? [];
      this.catalog_ids = initialValue.catalog_ids ?? {};
      this.zhihao = initialValue.zhihao;
      this.pictures = initialValue.pictures ?? [];
      this.stampIds = initialValue.stamps.map((id: any) => String(id));
      this.initialSnapshot = JSON.stringify(this.buildSnapshotData());
    }
  }

  private buildSnapshotData() {
    return {
      title: this.title,
      description: this.description,
      date_of_issue: this.date_of_issue,
      expiry_date: this.expiry_date,
      print_run: this.print_run,
      categories: this.categories,
      catalog_ids: this.catalog_ids,
      zhihao: this.zhihao,
      stampIds: this.stampIds,
    };
  }

  protected computeIsDirty(): boolean {
    if (this.initialSnapshot === null) return true;
    return this.picturesDirty || JSON.stringify(this.buildSnapshotData()) !== this.initialSnapshot;
  }

  validate(): [boolean, TranslationKey | ''] {
    if (this.title === "") return [false, 'validation.titleEmpty'];
    if (this.date_of_issue === null) return [false, 'validation.dateNotSet'];
    if (this.stampIds.length === 0) return [false, 'validation.noStampsSelected'];
    return validateZhihaoField(this.zhihao) ?? [true, ''];
  }

  setPictureFromPhone(picture: ImageType): void {
    this.pictures = [...this.pictures, picture];
    this.picturesDirty = true;
  }

  handleImageUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      this.pictures = [...this.pictures, { base64: e.target?.result as string }];
      this.picturesDirty = true;
    };
    reader.readAsDataURL(file);
  }

  removePicture(index: number): void {
    this.pictures = this.pictures.filter((_, i) => i !== index);
    this.picturesDirty = true;
  }

  reset() {
    this.title = "";
    this.description = "";
    this.date_of_issue = null;
    this.expiry_date = null;
    this.print_run = null;
    this.categories = [];
    this.catalog_ids = {};
    this.zhihao = undefined;
    this.pictures = [];
    this.stampIds = [];
  }
}
